import type { IOmit } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { ITablePaginationProps } from '~/components/molecules/TablePagination';
import type { IContainerProps } from '~/components/utils/Container';
import type { IUsePaginationResult } from '~/hooks/usePagination';
import { TablePagination } from '~/components/molecules/TablePagination';

export type IPaginationProps = IContainerProps &
  ITablePaginationProps & {
    pagination: IUsePaginationResult;
    total?: number;
  };

export const Pagination: React.FC<
  IOmit<
    IPaginationProps,
    | 'page'
    | 'rowsPerPage'
    | 'count'
    | 'onPageChange'
    | 'onRowsPerPageChange'
    | 'i18n'
  >
> = (props) => {
  const { pagination, total, ...other } = props;
  const { t } = useTranslation();

  return (
    <TablePagination
      {...other}
      page={pagination.page - 1}
      rowsPerPage={pagination.limit}
      count={total ?? 0}
      onPageChange={(_, page) => pagination.updatePage(page + 1)}
      onRowsPerPageChange={(limit) => {
        pagination.updateLimit(limit);
      }}
      i18n={{
        goTo: (type) => {
          const target =
            type === 'first'
              ? t('Pagination.firstPage')
              : type === 'last'
                ? t('Pagination.lastPage')
                : type === 'next'
                  ? t('Pagination.nextPage')
                  : t('Pagination.previousPage');
          return t('Pagination.goTo', { target });
        },
        displayedRows: ({ from, to, total }) =>
          t('Pagination.displayedRows', {
            from: String(from),
            to: String(to),
            total:
              total >= 0
                ? String(total)
                : t('Pagination.moreThan', {
                    total: String(total),
                  }),
          }),
        rowsPerPage: t('Pagination.rowsPerPage'),
      }}
    />
  );
};
